import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Smooths out height changes on a box that is sized by its content.
 *
 * A CSS transition cannot do this on its own: transitions fire when a computed value changes
 * through a style change, not when content reflows, so a content-sized box jumps. This measures the
 * height before and after and animates between the two, clipping overflow for the duration so the
 * content does not spill out of a box that is still shrinking.
 *
 * Resizes caused by the animation itself are ignored, otherwise the observer feeds itself.
 */
export const useAnimatedHeight = <T extends HTMLElement>(duration = 200) => {
  const teardown = useRef<() => void>();

  const attach = useCallback((element: T | null) => {
    teardown.current?.();
    teardown.current = undefined;

    if (!element || typeof ResizeObserver === 'undefined' || !element.animate) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    let previous = element.offsetHeight;
    let animation: Animation | undefined;

    const run = () => {
      const next = element.offsetHeight;
      if (next === previous) return;

      const from = previous;
      previous = next;

      const overflow = element.style.overflow;
      element.style.overflow = 'hidden';
      animation = element.animate(
        [{ height: `${from}px` }, { height: `${next}px` }],
        { duration, easing: 'ease-in-out' },
      );
      animation.finished
        .then(() => {
          element.style.overflow = overflow;
          // A resize that arrived mid-animation was skipped to avoid feeding the observer its own
          // output; pick it up now.
          run();
        })
        .catch(() => { element.style.overflow = overflow; });
    };

    const observer = new ResizeObserver(() => {
      if (animation?.playState === 'running') return;
      run();
    });

    observer.observe(element);

    teardown.current = () => {
      observer.disconnect();
      animation?.cancel();
    };
  }, [duration]);

  useEffect(() => () => teardown.current?.(), []);

  return attach;
};

export const useDebouncedState = <T>(
  defaultValue: T,
  delay = 500,
): [T, (value: T) => void, T, boolean] => {
  const [value, setValue] = useState(defaultValue);
  const [debouncedValue, setDebouncedValue] = useState(defaultValue);
  const timerRef = useRef<number>();

  useEffect(() => {
    timerRef.current = window.setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timerRef.current);
    };
  }, [value, delay]);

  return [value, setValue, debouncedValue, value !== debouncedValue];
};
